class SelectMenu extends HTMLElement {
  constructor() {
    super()

    this.supportsAnchorPositioning =
      'anchorName' in document.documentElement.style

    this.value = null
    this.selectedOptionWrapper = this.querySelector('[data-selected-option]')
    this.dropdownSelectedOptionWrapper = this.querySelector('[data-dropdown-selected-option]')
    this.inputs = [...this.querySelectorAll('input')]
    this.inputName = this.inputs[0].name
    this.originalInputs = [...document.querySelectorAll(`input[name="${this.inputName}"]`)]
    this.connectedInputs = [...this.originalInputs, ...document.querySelectorAll(`input[name^="${this.inputName}-"]`)]
    this.dropdown = this.querySelector('[popover]')

    this.clonedInput = this.hasAttribute('input-clone')
    this.onChange = this.handleChange.bind(this)
    this.onConnectedInputChange = this.handleConnectedInputChange.bind(this)
    this.onVariantChange = this.handleVariantChange.bind(this)

    this.handleUnsupportedResize = debounce(this.patchAnchorPositioning.bind(this), 200)
  }

  connectedCallback() {
    this.init()

    if (!this.supportsAnchorPositioning) {
      window.addEventListener('resize', this.handleUnsupportedResize)
      this.patchAnchorPositioning()
    }
  }

  disconnectedCallback() {
    this.destroy()

    if (!this.supportsAnchorPositioning) {
      window.removeEventListener('resize', this.handleUnsupportedResize)
    }
  }

  init() {
    this.addEventListener('change', this.onChange)

    if (this.clonedInput) {
      const inputSuffix = '-' + (this.getAttribute('input-clone-suffix') || 'clone')

      this.inputs.forEach(input => {
        input.name = input.name + inputSuffix
        if (input.checked) this.propagateInputState(input, false)
      })

      document.body.addEventListener('change', this.onConnectedInputChange)
      document.addEventListener('variant:change', this.onVariantChange)
    }

    this.updateSelection()
  }

  patchAnchorPositioning() {
    const dropdownPositionType = getComputedStyle(this.dropdown).getPropertyValue('position');

    let { top, bottom, height, width } = this.getBoundingClientRect()

    if (dropdownPositionType === 'absolute') {
      top += window.scrollY
      bottom += window.scrollY
    }

    bottom = window.innerHeight - bottom

    this.style.setProperty('--anchor-top', `${top}px`)
    this.style.setProperty('--anchor-bottom', `${bottom}px`)

    this.style.setProperty('--height', `${height}px`)
    this.style.setProperty('--width', `${width}px`)
  }

  destroy() {
    this.removeEventListener('change', this.onChange)

    if (this.clonedInput) {
      this.inputs.forEach(input => input.name = this.inputName)

      document.body.removeEventListener('change', this.onConnectedInputChange)
      document.removeEventListener('variant:change', this.onVariantChange)
    }
  }

  handleVariantChange(event) {
    for (const input of this.inputs) {
      const originalInput = this.getOriginalInput(input)
      const originalInputDisabled = originalInput.classList.contains('disabled')
      input.classList.toggle('disabled', originalInputDisabled)
      input.parentElement.querySelector('.select-menu__option-text').classList.toggle('is-disabled', originalInputDisabled)

      if (this.value === input.value) {
        this.updateSelection(input)
      }
    }
  }

  handleConnectedInputChange(event) {
    if (!this.connectedInputs.includes(event.target)) return

    const localInput = this.getLocalInput(event.target)
    if (localInput) {
      localInput.checked = event.target.checked
      this.updateSelection(localInput)
    }
  }

  getMirroredInputs(input) {
    return this.connectedInputs?.filter(i => i.value === input.value && i !== input) ?? []
  }

  getLocalInput(input) {
    return this.inputs.find(i => i.value === input.value)
  }

  getOriginalInput(input) {
    return this.originalInputs.find(i => i.value === input.value)
  }

  handleChange(event) {
    const input = event.target
    this.propagateInputState(input)

    this.updateSelection(input)
    this.close()
  }

  propagateInputState(input, dispatch = true) {
    for (const mirroredInput of this.getMirroredInputs(input)) {
      mirroredInput.checked = input.checked

      if (mirroredInput.name !== this.inputName) {
        // Update selection on additional cloned inputs
        mirroredInput.closest('select-menu')?.updateSelection(mirroredInput)
      } else if (dispatch) {
        // Dispatch change event to trigger source input change handler
        mirroredInput.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
  }

  updateSelection(input) {
    input ??= this.inputs.find(input => input.checked)

    if (!input) return

    this.value = input.value
    const label = this.querySelector(`label[for="${input.id}"]`)
    this.updateSelectionMarkup(label.innerHTML)
  }

  updateSelectionMarkup(content) {
    this.selectedOptionWrapper.innerHTML = content

    if (this.dropdownSelectedOptionWrapper) {
      this.dropdownSelectedOptionWrapper.innerHTML = content
    }
  }

  open() {
    this.dropdown.showPopover()
  }

  close() {
    this.dropdown.hidePopover()
  }
}

if (!customElements.get('select-menu')) {
  customElements.define('select-menu', SelectMenu)
}
